/**
 * Right-side drawer built on Radix Dialog primitives.
 * Used for detail views (leave request, invoice, payslip, etc.)
 */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;

const DrawerOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn('fixed inset-0 z-40 bg-slate-900/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)}
		{...props}
	/>
));
DrawerOverlay.displayName = 'DrawerOverlay';

const DrawerContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<DialogPrimitive.Portal>
		<DrawerOverlay />
		<DialogPrimitive.Content
			ref={ref}
			className={cn(
				'fixed inset-y-0 right-0 z-50 flex w-[460px] max-w-full flex-col overflow-y-auto bg-white shadow-[-10px_0_40px_rgba(0,0,0,0.12)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
				className
			)}
			{...props}
		>
			{children}
		</DialogPrimitive.Content>
	</DialogPrimitive.Portal>
));
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('sticky top-0 z-10 flex items-center gap-2 border-b bg-white px-5 py-4', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex gap-2 border-t bg-white px-5 py-3.5', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title ref={ref} className={cn('text-[15px] font-semibold leading-tight', className)} {...props} />
));
DrawerTitle.displayName = 'DrawerTitle';

/** Standalone close button — place on the right of the header. */
function DrawerCloseButton({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
	return (
		<DialogPrimitive.Close
			className={cn('ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-white text-muted-foreground hover:bg-background hover:text-foreground', className)}
			{...props}
		>
			<X className="h-3.5 w-3.5" />
		</DialogPrimitive.Close>
	);
}

export { Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerFooter, DrawerTitle, DrawerClose, DrawerTrigger, DrawerCloseButton };
